<template>
  <div class="cts-section">
    <div class="cts-header">
      <span class="cts-group-title">Equipo de mantenimiento ({{ activeTechnicians.length }} activos)</span>
      <button class="cts-add-btn" @click="startAdd">＋ Agregar técnico</button>
    </div>

    <div v-if="!activeTechnicians.length && !showAddForm" class="cts-empty">
      <span class="mdi mdi-account-group-outline cts-empty-icon"></span>
      <span class="cts-empty-title">Sin técnicos registrados</span>
    </div>

    <div v-for="t in activeTechnicians" :key="t.id" class="cts-card">
      <template v-if="editingId !== t.id">
        <div class="cts-card-top">
          <div class="cts-card-avatar-wrap">
            <span class="cts-card-avatar" :style="{ background: AVATAR_COLORS[t.avatarColor] || t.avatarColor }">{{ initials(t.name) }}</span>
            <span class="cts-card-presence" :class="`cts-card-presence--${t.status.toLowerCase()}`"></span>
          </div>
          <div class="cts-card-identity">
            <div class="cts-card-name">{{ t.name }}</div>
            <div class="cts-card-specialty">{{ specialtyLabel(t) }} · {{ t.cargo || 'Sin cargo definido' }}</div>
          </div>
          <div class="cts-card-actions">
            <button class="cts-icon-btn" @click="startEdit(t)"><span class="mdi mdi-pencil-outline"></span></button>
            <div class="cts-more-wrap">
              <button class="cts-icon-btn" @click="toggleMoreMenu(t.id)"><span class="mdi mdi-dots-vertical"></span></button>
              <div v-if="moreMenuId === t.id" class="cts-more-menu">
                <button @click="onDeactivate(t)">Desactivar técnico</button>
              </div>
            </div>
          </div>
        </div>

        <div class="cts-card-contact-row">
          <span v-if="t.phone" class="cts-contact-item"><span class="mdi mdi-phone-outline"></span> {{ t.phone }}</span>
          <span v-if="t.email" class="cts-contact-item"><span class="mdi mdi-email-outline"></span> {{ t.email }}</span>
          <span class="cts-contact-item"><span class="mdi mdi-cash"></span> {{ t.hourlyRate ? formatCurrency(t.hourlyRate) + '/hr' : 'Sin tarifa' }}</span>
        </div>

        <div class="cts-capacity-row">
          <span class="cts-capacity-label">{{ t.activeTicketCount }} de {{ t.maxConcurrentTickets }} tickets activos</span>
          <div class="cts-capacity-bar"><div class="cts-capacity-fill" :class="capacityClass(t.capacityPercent)" :style="{ width: t.capacityPercent + '%' }"></div></div>
        </div>

        <div class="cts-history-row">
          <span class="cts-history-item">{{ t.last30DaysTicketCount }} tickets atendidos (30 días)</span>
          <span class="cts-history-item">{{ t.avgResolutionHoursHistorical !== null ? `${t.avgResolutionHoursHistorical}h promedio histórico` : 'Sin historial de resolución' }}</span>
          <span class="cts-history-item" :class="slaClass(t.slaCompliancePercent)">{{ t.slaCompliancePercent !== null ? `${t.slaCompliancePercent}% SLA cumplido` : 'Sin datos de SLA' }}</span>
        </div>
      </template>

      <div v-else class="cts-form">
        <div class="cts-form-columns">
          <div class="cts-form-col">
            <span class="cts-form-col-title">Identidad</span>
            <input v-model="form.name" class="cts-input" placeholder="Nombre completo" />
            <select v-model="form.specialty" class="cts-input">
              <option value="">Selecciona especialidad</option>
              <option value="ELECTRICAL">Electricidad</option>
              <option value="PLUMBING">Plomería</option>
              <option value="HVAC">HVAC y climatización</option>
              <option value="CIVIL_WORK">Obra civil</option>
              <option value="FIRE_SYSTEMS">Sistemas contra incendios</option>
              <option value="GENERAL">General</option>
              <option value="OTHER">Otro</option>
            </select>
            <input v-if="form.specialty === 'OTHER'" v-model="form.specialtyOther" class="cts-input" placeholder="Especifica la especialidad" />
            <input v-model="form.cargo" class="cts-input" placeholder="Cargo" />
            <input v-model="form.phone" class="cts-input" placeholder="Teléfono" />
            <input v-model="form.email" class="cts-input" placeholder="Correo electrónico" />
            <div class="cts-color-picker">
              <button v-for="c in AVATAR_COLOR_KEYS" :key="c" class="cts-color-swatch" :class="{ 'cts-color-swatch--active': form.avatarColor === c }" :style="{ background: AVATAR_COLORS[c] }" @click="form.avatarColor = c"></button>
            </div>
          </div>

          <div class="cts-form-col">
            <span class="cts-form-col-title">Configuración operativa</span>
            <div class="cts-field-row">
              <span class="cts-field-label">Capacidad máxima</span>
              <div class="cts-stepper">
                <button @click="form.maxConcurrentTickets = Math.max(1, form.maxConcurrentTickets - 1)">−</button>
                <span>{{ form.maxConcurrentTickets }}</span>
                <button @click="form.maxConcurrentTickets = Math.min(10, form.maxConcurrentTickets + 1)">+</button>
              </div>
            </div>
            <div class="cts-pills">
              <button class="cts-pill" :class="{ 'cts-pill--active': form.contractType === 'EMPLOYEE' }" @click="form.contractType = 'EMPLOYEE'">Empleado directo</button>
              <button class="cts-pill" :class="{ 'cts-pill--active': form.contractType === 'EXTERNAL' }" @click="form.contractType = 'EXTERNAL'">Proveedor externo</button>
            </div>
            <input v-model.number="form.hourlyRate" type="number" class="cts-input" placeholder="Tarifa por hora (MXN)" />
            <div class="cts-days">
              <button v-for="d in DAYS" :key="d.value" class="cts-day-btn" :class="{ 'cts-day-btn--active': form.availableDays.includes(d.value) }" @click="toggleDay(d.value)">{{ d.label }}</button>
            </div>
            <label class="cts-toggle-row"><input type="checkbox" v-model="form.flexibleSchedule" /> Horario flexible</label>
            <div v-if="!form.flexibleSchedule" class="cts-field-row">
              <input v-model="form.availableStartTime" type="time" class="cts-input" />
              <input v-model="form.availableEndTime" type="time" class="cts-input" />
            </div>

            <span class="cts-form-col-title" style="margin-top: 0.5rem;">Acceso y notas</span>
            <label class="cts-toggle-row"><input type="checkbox" v-model="form.platformAccessEnabled" /> Dar acceso a la plataforma</label>
            <input v-if="form.platformAccessEnabled" v-model="form.platformUsername" class="cts-input" placeholder="Usuario" />
            <textarea v-model="form.notes" class="cts-textarea" rows="2" placeholder="Notas internas..."></textarea>
          </div>
        </div>

        <div class="cts-form-actions">
          <button class="cts-cancel-btn" @click="cancelEdit(t)">Cancelar</button>
          <button class="cts-save-btn" :disabled="!form.name || !form.phone" @click="saveTechnician(t)">Guardar técnico</button>
        </div>
      </div>
    </div>

    <template v-if="inactiveTechnicians.length">
      <button class="cts-inactive-toggle" @click="showInactive = !showInactive">
        <span class="mdi" :class="showInactive ? 'mdi-chevron-down' : 'mdi-chevron-right'"></span>
        Técnicos inactivos ({{ inactiveTechnicians.length }})
      </button>
      <div v-if="showInactive" class="cts-inactive-list">
        <div v-for="t in inactiveTechnicians" :key="t.id" class="cts-inactive-row">
          <span class="cts-card-avatar cts-card-avatar--small" :style="{ background: AVATAR_COLORS[t.avatarColor] || t.avatarColor }">{{ initials(t.name) }}</span>
          <span class="cts-inactive-name">{{ t.name }}</span>
          <button class="cts-reactivate-btn" @click="onReactivate(t)">Reactivar</button>
        </div>
      </div>
    </template>

    <Transition name="cts-toast-fade">
      <div v-if="toastMessage" class="cts-toast"><span class="mdi mdi-check-circle"></span> {{ toastMessage }}</div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { getTechniciansForManager, createTechnician, updateTechnician } from '@/services/maintenance'

const props = defineProps({ parkId: { type: String, required: true } })
const emit = defineEmits(['dirty-change'])

const AVATAR_COLORS = { blue: '#3b82f6', green: '#10b981', violet: '#8b5cf6', orange: '#f97316', teal: '#14b8a6', pink: '#ec4899', indigo: '#6366f1', amber: '#f0a500' }
const AVATAR_COLOR_KEYS = Object.keys(AVATAR_COLORS)
const SPECIALTY_LABELS = { ELECTRICAL: 'Electricidad', PLUMBING: 'Plomería', HVAC: 'HVAC y climatización', CIVIL_WORK: 'Obra civil', FIRE_SYSTEMS: 'Sistemas contra incendios', GENERAL: 'General' }
const DAYS = [{ value: 'L', label: 'L' }, { value: 'M', label: 'M' }, { value: 'X', label: 'X' }, { value: 'J', label: 'J' }, { value: 'V', label: 'V' }, { value: 'S', label: 'S' }, { value: 'D', label: 'D' }]

const technicians = ref([])
const editingId = ref(null)
const showAddForm = ref(false)
const showInactive = ref(false)
const moreMenuId = ref(null)
const toastMessage = ref(null)

const emptyForm = () => ({
  name: '', cargo: '', avatarColor: 'blue', specialty: '', specialtyOther: '', phone: '', email: '',
  maxConcurrentTickets: 3, contractType: null, hourlyRate: null,
  availableDays: [], availableStartTime: '', availableEndTime: '', flexibleSchedule: false,
  platformAccessEnabled: false, platformUsername: '', notes: '',
})
const form = ref(emptyForm())

async function load() {
  technicians.value = await getTechniciansForManager(props.parkId)
}
onMounted(load)

const activeTechnicians = computed(() => technicians.value.filter((t) => t.active))
const inactiveTechnicians = computed(() => technicians.value.filter((t) => !t.active))

function initials(name) { return name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase() }
function specialtyLabel(t) { return t.specialty === 'OTHER' ? (t.specialtyOther || 'Otro') : (SPECIALTY_LABELS[t.specialty] || 'Sin especialidad') }
function capacityClass(pct) { return pct >= 85 ? 'cts-capacity-fill--red' : pct >= 60 ? 'cts-capacity-fill--amber' : 'cts-capacity-fill--green' }
function slaClass(pct) { if (pct === null) return ''; return pct >= 90 ? 'cts-history-item--green' : pct >= 70 ? 'cts-history-item--amber' : 'cts-history-item--red' }
function formatCurrency(n) { return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n || 0) }

function toggleDay(d) {
  const i = form.value.availableDays.indexOf(d)
  if (i >= 0) form.value.availableDays.splice(i, 1)
  else form.value.availableDays.push(d)
}

function toggleMoreMenu(id) { moreMenuId.value = moreMenuId.value === id ? null : id }

function startEdit(t) {
  form.value = {
    name: t.name, cargo: t.cargo || '', avatarColor: t.avatarColor, specialty: t.specialty || '', specialtyOther: t.specialtyOther || '',
    phone: t.phone || '', email: t.email || '', maxConcurrentTickets: t.maxConcurrentTickets ?? 3, contractType: t.contractType,
    hourlyRate: t.hourlyRate, availableDays: [...(t.availableDays || [])], availableStartTime: t.availableStartTime || '',
    availableEndTime: t.availableEndTime || '', flexibleSchedule: !!t.flexibleSchedule, platformAccessEnabled: !!t.platformAccessEnabled,
    platformUsername: t.platformUsername || '', notes: t.notes || '',
  }
  editingId.value = t.id
  moreMenuId.value = null
  emit('dirty-change', true)
}

function startAdd() {
  const newT = { id: 'new', name: '', active: true, status: 'AVAILABLE', avatarColor: 'blue', activeTicketCount: 0, capacityPercent: 0, maxConcurrentTickets: 3, last30DaysTicketCount: 0, avgResolutionHoursHistorical: null, slaCompliancePercent: null }
  technicians.value.push(newT)
  showAddForm.value = true
  startEdit(newT)
}

function cancelEdit(t) {
  if (t.id === 'new') {
    technicians.value = technicians.value.filter((x) => x.id !== 'new')
    showAddForm.value = false
  }
  editingId.value = null
  emit('dirty-change', false)
}

function showToast(msg) {
  toastMessage.value = msg
  setTimeout(() => (toastMessage.value = null), 2500)
}

async function saveTechnician(t) {
  if (t.id === 'new') {
    await createTechnician(form.value)
    showAddForm.value = false
  } else {
    await updateTechnician(t.id, form.value)
  }
  editingId.value = null
  emit('dirty-change', false)
  await load()
  showToast('Técnico guardado')
}

async function onDeactivate(t) {
  moreMenuId.value = null
  if (!confirm(`¿Desactivar a ${t.name}?`)) return
  await updateTechnician(t.id, { active: false })
  await load()
}

async function onReactivate(t) {
  await updateTechnician(t.id, { active: true })
  await load()
}
</script>

<style scoped>
.cts-section { display: flex; flex-direction: column; gap: 0.75rem; max-width: 760px; }
.cts-header { display: flex; align-items: center; justify-content: space-between; }
.cts-group-title { font-family: var(--font-body); font-size: 12px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.8px; color: #64748b; }
.cts-add-btn { background: #3b82f6; color: #ffffff; border: none; border-radius: 6px; padding: 0.4rem 0.75rem; font-family: var(--font-body); font-size: 12px; font-weight: 500; cursor: pointer; }

.cts-empty { display: flex; flex-direction: column; align-items: center; gap: 0.4rem; padding: 2rem; }
.cts-empty-icon { font-size: 32px; color: #cbd5e1; }
.cts-empty-title { font-family: var(--font-body); font-size: 13px; color: #64748b; }

.cts-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 0.75rem; display: flex; flex-direction: column; gap: 0.5rem; }

.cts-card-top { display: flex; align-items: center; gap: 0.6rem; }
.cts-card-avatar-wrap { position: relative; flex-shrink: 0; }
.cts-card-avatar { width: 36px; height: 36px; border-radius: 50%; color: #ffffff; font-size: 12px; font-weight: 600; display: flex; align-items: center; justify-content: center; }
.cts-card-avatar--small { width: 24px; height: 24px; font-size: 10px; }
.cts-card-presence { position: absolute; bottom: -1px; right: -1px; width: 8px; height: 8px; border-radius: 50%; border: 2px solid #ffffff; }
.cts-card-presence--available { background: #10b981; }
.cts-card-presence--working { background: #f0a500; }
.cts-card-presence--inactive { background: #94a3b8; }

.cts-card-identity { flex: 1; min-width: 0; }
.cts-card-name { font-family: var(--font-body); font-size: 14px; font-weight: 500; color: #1e3a5f; }
.cts-card-specialty { font-family: var(--font-body); font-size: 11px; color: #94a3b8; }

.cts-card-actions { display: flex; gap: 0.3rem; }
.cts-icon-btn { background: none; border: none; color: #94a3b8; cursor: pointer; display: flex; }
.cts-more-wrap { position: relative; }
.cts-more-menu { position: absolute; top: 100%; right: 0; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; box-shadow: 0 8px 24px rgba(15,23,42,0.12); padding: 0.3rem; z-index: 50; min-width: 160px; }
.cts-more-menu button { display: block; width: 100%; text-align: left; background: none; border: none; font-family: var(--font-body); font-size: 12px; color: #dc2626; padding: 0.4rem 0.5rem; cursor: pointer; border-radius: 5px; }
.cts-more-menu button:hover { background: #fef2f2; }

.cts-card-contact-row { display: flex; flex-wrap: wrap; gap: 0.9rem; font-family: var(--font-body); font-size: 11px; color: #64748b; padding-left: 44px; }
.cts-contact-item { display: flex; align-items: center; gap: 0.25rem; }

.cts-capacity-row { padding-left: 44px; }
.cts-capacity-label { font-family: var(--font-body); font-size: 11px; color: #64748b; }
.cts-capacity-bar { height: 4px; background: #e2e8f0; border-radius: 2px; margin-top: 0.2rem; overflow: hidden; max-width: 220px; }
.cts-capacity-fill { height: 100%; }
.cts-capacity-fill--green { background: #10b981; }
.cts-capacity-fill--amber { background: #f0a500; }
.cts-capacity-fill--red { background: #e84545; }

.cts-history-row { display: flex; flex-wrap: wrap; gap: 0.9rem; padding-left: 44px; font-family: var(--font-body); font-size: 10px; color: #94a3b8; }
.cts-history-item--green { color: #16a34a; }
.cts-history-item--amber { color: #d97706; }
.cts-history-item--red { color: #dc2626; }

.cts-form { display: flex; flex-direction: column; gap: 0.6rem; }
.cts-form-columns { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; }
.cts-form-col { display: flex; flex-direction: column; gap: 0.4rem; }
.cts-form-col-title { font-family: var(--font-body); font-size: 10px; text-transform: uppercase; color: #94a3b8; }

.cts-input, .cts-textarea { font-family: var(--font-body); font-size: 12px; border: 1px solid #e2e8f0; border-radius: 6px; padding: 0.4rem 0.5rem; box-sizing: border-box; width: 100%; }
.cts-textarea { resize: vertical; }

.cts-color-picker { display: flex; gap: 0.3rem; }
.cts-color-swatch { width: 22px; height: 22px; border-radius: 50%; border: 2px solid transparent; cursor: pointer; }
.cts-color-swatch--active { border-color: #1e3a5f; }

.cts-field-row { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; }
.cts-field-label { font-family: var(--font-body); font-size: 12px; color: #64748b; }
.cts-stepper { display: flex; align-items: center; gap: 0.5rem; }
.cts-stepper button { width: 22px; height: 22px; border-radius: 50%; border: 1px solid #e2e8f0; background: #f8f9fc; cursor: pointer; font-size: 13px; }
.cts-stepper span { font-family: var(--font-mono); font-size: 12px; min-width: 14px; text-align: center; }

.cts-pills { display: flex; gap: 0.3rem; }
.cts-pill { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 20px; padding: 0.25rem 0.6rem; font-family: var(--font-body); font-size: 11px; color: #64748b; cursor: pointer; }
.cts-pill--active { background: #eff6ff; border-color: #3b82f6; color: #3b82f6; font-weight: 500; }

.cts-days { display: flex; gap: 0.25rem; }
.cts-day-btn { width: 26px; height: 26px; border-radius: 50%; border: 1px solid #e2e8f0; background: #ffffff; color: #64748b; font-family: var(--font-body); font-size: 10px; cursor: pointer; }
.cts-day-btn--active { background: #3b82f6; border-color: #3b82f6; color: #ffffff; }

.cts-toggle-row { display: flex; align-items: center; gap: 0.4rem; font-family: var(--font-body); font-size: 12px; color: #1e3a5f; }

.cts-form-actions { display: flex; justify-content: flex-end; gap: 0.5rem; }
.cts-cancel-btn { background: none; border: none; color: #64748b; font-family: var(--font-body); font-size: 12px; cursor: pointer; }
.cts-save-btn { background: #3b82f6; color: #ffffff; border: none; border-radius: 6px; padding: 0.5rem 1rem; font-family: var(--font-body); font-size: 12px; font-weight: 500; cursor: pointer; }
.cts-save-btn:disabled { opacity: 0.4; cursor: default; }

.cts-inactive-toggle { display: flex; align-items: center; gap: 0.3rem; background: none; border: none; font-family: var(--font-body); font-size: 12px; color: #94a3b8; cursor: pointer; padding: 0.5rem 0; }
.cts-inactive-list { display: flex; flex-direction: column; gap: 0.3rem; }
.cts-inactive-row { display: flex; align-items: center; gap: 0.5rem; height: 40px; background: #f8f9fc; border-radius: 6px; padding: 0 0.6rem; opacity: 0.7; }
.cts-inactive-name { flex: 1; font-family: var(--font-body); font-size: 12px; color: #64748b; }
.cts-reactivate-btn { background: none; border: none; color: #3b82f6; font-family: var(--font-body); font-size: 11px; cursor: pointer; }

.cts-toast { position: fixed; top: 1rem; right: 1rem; display: flex; align-items: center; gap: 0.4rem; background: #1e3a5f; color: #ffffff; padding: 0.6rem 1rem; border-radius: 8px; font-family: var(--font-body); font-size: 13px; box-shadow: 0 8px 24px rgba(0,0,0,0.2); z-index: 3000; }
.cts-toast .mdi { color: #10b981; }
.cts-toast-fade-enter-active, .cts-toast-fade-leave-active { transition: opacity 300ms ease; }
.cts-toast-fade-enter-from, .cts-toast-fade-leave-to { opacity: 0; }
</style>